import axios from "axios";

// Default to a relative `/api` so all calls are same-origin with the Vite dev
// server (which proxies /api, /central, /original, /done, /ready, /identify,
// /edited, /viz, /attention, /training, /static, /media to VITE_PROXY_TARGET —
// see vite.config.js). Override with VITE_OPERATOR_API to hit a different
// backend directly (and accept the resulting CORS dance).
const API_BASE = import.meta.env.VITE_OPERATOR_API || "/api";

// Enable credentials (cookies) for all requests - required for session auth
axios.defaults.withCredentials = true;

// Helper to get CSRF token from cookies
function getCsrfToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Add CSRF token to all non-GET requests
axios.interceptors.request.use((config) => {
  if (config.method !== "get") {
    const token = getCsrfToken();
    if (token) {
      config.headers["X-CSRFToken"] = token;
    }
  }
  return config;
});

// =============================================================================
// Authentication API Functions
// =============================================================================

export async function authLogin(username, password) {
  const res = await axios.post(
    `${API_BASE}/auth/login/`,
    { username, password },
    { withCredentials: true }
  );
  return res.data;
}

export async function authLogout() {
  const res = await axios.post(
    `${API_BASE}/auth/logout/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function authMe() {
  const res = await axios.get(
    `${API_BASE}/auth/me/`,
    { withCredentials: true }
  );
  return res.data;
}

export async function authStatus() {
  const res = await axios.get(
    `${API_BASE}/auth/status/`,
    { withCredentials: true }
  );
  return res.data;
}

// =============================================================================
// Profile API Functions
// =============================================================================

export async function fetchProfiles() {
  const res = await axios.get(`${API_BASE}/profiles/`, { withCredentials: true });
  return res.data;
}

export async function fetchProfile(profileId) {
  const res = await axios.get(`${API_BASE}/profiles/${profileId}/`, { withCredentials: true });
  return res.data;
}

export async function createProfile(name, adjustments, companyWide = false, profileType = 'delta') {
  const res = await axios.post(
    `${API_BASE}/profiles/`,
    { name, adjustments, company_wide: companyWide, profile_type: profileType },
    { withCredentials: true }
  );
  return res.data;
}

export async function updateProfile(profileId, data) {
  const res = await axios.patch(
    `${API_BASE}/profiles/${profileId}/`,
    data,
    { withCredentials: true }
  );
  return res.data;
}

export async function deleteProfile(profileId) {
  const res = await axios.delete(
    `${API_BASE}/profiles/${profileId}/`,
    { withCredentials: true }
  );
  return res.data;
}

export async function applyProfileToPhotos(profileId, photoIds) {
  const res = await axios.post(
    `${API_BASE}/profiles/${profileId}/apply/`,
    { photo_ids: photoIds },
    { withCredentials: true }
  );
  return res.data;
}

export async function importLightroomProfile(name, xmpContent) {
  const res = await axios.post(
    `${API_BASE}/profiles/import/lightroom/`,
    { name, xmp_content: xmpContent },
    { withCredentials: true }
  );
  return res.data;
}

// =============================================================================
// Photo API Functions
// =============================================================================

export async function fetchUnlabeled(limit = 20, offset = 0, image_id = null) {
  const params = { limit, offset, person__isnull: "true" };
  if (image_id) params.image_id = image_id;
  const res = await axios.get(`${API_BASE}/faces/`, { params });
  return res.data;
}

export async function searchNeighbors(embedding, top_k = 6) {
  const emb =
    Array.isArray(embedding)
      ? embedding
      : typeof embedding === "string"
        ? JSON.parse(embedding)
        : [];
  const res = await axios.post(`${API_BASE}/faces/search/`, {
    embedding: emb,
    top_k,
  });
  return res.data.results || [];
}

/**
 * Near-threshold person suggestions for labeling one face.
 * GET /api/faces/{id}/candidates/ — server-side dedup (one row per person),
 * sorted by distance, filtered (labelled, not soft-deleted, not ignored).
 * Each row: { person_id, person_name, team_memberships, distance,
 * confidence, source } where confidence is the shared auto-match curve —
 * the same percentage the face pill shows after assignment.
 */
export async function getFaceCandidates(faceId, limit = 8) {
  const res = await axios.get(`${API_BASE}/faces/${faceId}/candidates/`, {
    params: { limit },
  });
  return res.data.candidates || [];
}

export async function fetchPhotosWithUnlabeled(limit = 100, offset = 0) {
  const res = await axios.get(`${API_BASE}/photos/`, {
    params: { limit, offset, has_unlabeled_faces: "true" },
  });
  return res.data;
}

export async function fetchIncompletePhotos(limit = 100, offset = 0) {
  const res = await axios.get(`${API_BASE}/photos/`, {
    params: { limit, offset, complete: "false" },
  });
  return res.data;
}

export async function fetchReadyCropPhotos(limit = 100, offset = 0) {
  const res = await axios.get(`${API_BASE}/photos/`, {
    params: { limit, offset, complete: "false", status: "done" },
  });
  return res.data;
}

export async function fetchAllPhotos(limit = 100, offset = 0) {
  const extra = arguments.length > 2 && arguments[2] ? arguments[2] : {};
  // Tank only edits pipeline photos; hide field-ingested photos unless a
  // caller passed its own ingestion_mode explicitly.
  const params = { ingestion_mode: 'pipeline', limit, offset, ...extra };
  const res = await axios.get(`${API_BASE}/photos/`, {
    params,
  });
  return res.data;
}

export async function fetchPhotoStats() {
  // Match the grid's filter (usePhotoList sends ingestion_mode=pipeline)
  // so the overview pill counts don't include field-ingested photos the
  // grid hides.
  const res = await axios.get(`${API_BASE}/photos/stats/?ingestion_mode=pipeline`);
  return res.data;
}

export async function fetchIngestionStats(ingestionMode) {
  const url = ingestionMode
    ? `${API_BASE}/photos/ingestion_stats/?ingestion_mode=${encodeURIComponent(ingestionMode)}`
    : `${API_BASE}/photos/ingestion_stats/`;
  const res = await axios.get(url);
  return res.data;
}

export async function fetchIngestionFailures() {
  const res = await axios.get(`${API_BASE}/ingestion-failures/`, {
    withCredentials: true,
  });
  return res.data;
}

export async function fetchEnvironmentStatus() {
  const res = await axios.get(`${API_BASE}/matches/environment_status/`);
  return res.data;
}

export async function emptyEnvironment({ force = false } = {}) {
  const res = await axios.post(`${API_BASE}/photos/empty_environment/`, { force });
  return res.data;
}

export async function fetchPhotoIds(params = {}) {
  // Fetch all image_ids matching current filters (for Select All)
  const res = await axios.get(`${API_BASE}/photos/ids/`, { params });
  return res.data;
}

export async function fetchMetricsRate() {
  const res = await axios.get(`${API_BASE}/photos/metrics_rate/`);
  return res.data;
}

export async function fetchMetricsStats(fromDate = null, toDate = null) {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  const res = await axios.get(`${API_BASE}/photos/metrics_stats/`, { params });
  return res.data;
}

export async function fetchCameraStats(limit = 500) {
  const res = await axios.get(`${API_BASE}/photos/camera_stats/`, { params: { limit } });
  return res.data;
}

export async function searchPersons(query, matchId = null) {
  if (!query) return [];
  const params = { q: query };
  if (matchId) params.match_id = matchId;
  const res = await axios.get(`${API_BASE}/persons/search/`, { params });
  return res.data;
}

/**
 * Bulk-link multiple persons to a team in a single request (P6).
 * `rows` is `[{person_id, jersey_number?}, ...]`; backend returns
 * `{results: [{person_id, status, jersey_number?, error?}], summary: {...}}`
 * with per-row status so each row can render with a badge.
 */
export async function bulkLinkTeamMembers(teamId, rows) {
  const res = await axios.post(
    `${API_BASE}/teams/${teamId}/bulk_link_members/`,
    { rows },
    { withCredentials: true },
  );
  return res.data;
}

export async function fetchPhoto(imageId) {
  const res = await axios.get(`${API_BASE}/photos/${imageId}/`);
  return res.data;
}

export async function fetchPhotoMeta(imageId) {
  if (!imageId) return null;
  const res = await axios.get(`${API_BASE}/photos/${imageId}/meta/`);
  return res.data;
}

/**
 * Save a photo's adjustments.
 *
 * `adjustments` are ABSOLUTE slider values (profile baseline fused with the
 * editor's changes). `adjustmentsDelta` is the editor's MOVEMENT only, for
 * FTP destinations that opt out of the company profile and need the editor's
 * changes applied to the untouched original. See lib/adjustments-delta.js.
 *
 * The delta is always sent, empty included, so a photo the editor moved back
 * onto its baseline overwrites a previously stored delta instead of leaving
 * a stale one behind.
 *
 * @param {string} imageId
 * @param {Object} adjustments - Absolute values
 * @param {Object} [adjustmentsDelta={}] - Movement-only delta
 */
export async function updatePhotoAdjustments(imageId, adjustments, adjustmentsDelta = {}) {
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/adjustments/`, {
    adjustments,
    adjustments_delta: adjustmentsDelta || {},
  });
  return res.data;
}

/**
 * Apply IPTC and/or Develop settings to every non-final photo in a match.
 *
 * Tank Redesign: design_handoff_tank/README.md § "Apply to non-final" —
 * never overwrites Done / Exported / Skipped photos. Backend hard-caps
 * the status filter to ['pending', 'attention'].
 *
 * @param {Object} payload
 * @param {string} payload.matchId
 * @param {{ adjustments?: Object, iptc?: Object }} payload.settings
 * @param {string[]} [payload.statusFilter=['pending','attention']]
 * @returns {Promise<{ ok: boolean, match_id: string, updated_count: number, status_filter: string[] }>}
 */
export async function applyToNonFinal({ matchId, settings, statusFilter }) {
  const res = await axios.post(`${API_BASE}/photos/apply_to_non_final/`, {
    match_id: matchId,
    settings,
    status_filter: statusFilter,
  });
  return res.data;
}

/**
 * Active sessions for the current user (Tank Redesign Me screen).
 *
 * @returns {Promise<{ sessions: Array<{
 *   id: string,
 *   session_type: 'tank'|'ftp'|'api',
 *   ip_address: string,
 *   city: string,
 *   country_code: string,
 *   country_name: string,
 *   started_at: string|null,
 *   last_activity: string|null,
 *   is_active: boolean,
 *   user_agent: string,
 * }> }>}
 */
export async function fetchSessions() {
  const res = await axios.get(`${API_BASE}/auth/sessions/`);
  return res.data;
}

export async function fetchLabeledPhotos(limit = 100, offset = 0) {
  const res = await axios.get(`${API_BASE}/photos/`, {
    params: { limit, offset, has_unlabeled_faces: "false" },
  });
  return res.data;
}

export async function fetchFacesByImage(imageId, limit = 200, offset = 0, includeIgnored = false) {
  if (!imageId) return [];
  const params = { image_id: imageId, limit, offset };
  if (includeIgnored) params.include_ignored = "true";
  const res = await axios.get(`${API_BASE}/faces/`, { params });
  return res.data;
}

export async function fetchFacesByPerson(personId, { limit = 500, offset = 0 } = {}) {
  if (!personId) return [];
  const params = { person: personId, limit, offset, ordering: "-embedding_quality" };
  const res = await axios.get(`${API_BASE}/faces/`, { params });
  return res.data;
}

export async function addPersonToPhoto(imageId, personId, position = null) {
  const data = { person_id: personId };
  if (position !== null) {
    data.position = position;
  }
  const res = await axios.post(`${API_BASE}/photos/${imageId}/add_person/`, data);
  return res.data;
}

export async function removePersonFromPhoto(imageId, personId) {
  const res = await axios.post(`${API_BASE}/photos/${imageId}/remove_person/`, {
    person_id: personId,
  });
  return res.data;
}

export async function updatePhotoCaption(imageId, captionOverride, captionChains = undefined) {
  const payload = { caption_override: captionOverride };
  if (captionChains !== undefined) payload.caption_chains = captionChains;
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/caption/`, payload);
  return res.data;
}

export async function updatePhotoPersonOrder(imageId, personOrder) {
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/`, {
    person_order: Array.isArray(personOrder) ? personOrder : [],
  });
  return res.data;
}

export async function updatePhotoPrimarySubject(imageId, personId) {
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/`, {
    primary_subject_person_id: personId || null,
  });
  return res.data;
}

export async function updatePhotoKeywords(imageId, keywordsOverride) {
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/keywords/`, {
    keywords_override: keywordsOverride,
  });
  return res.data;
}

export async function completePhoto(imageId, autoFtp = false, approvedCrop = false, select = false) {
  // `select` is the wire choice (Done + Wire) — decided PER COMPLETION,
  // never sticky. The server gates it (custom caption) and 400s with
  // {code: "custom_caption_required" | "no_selects_target"} before any
  // mutation, so a rejected send leaves the photo untouched.
  const res = await axios.post(`${API_BASE}/photos/${imageId}/complete/`, {
    auto_ftp: autoFtp,
    approved_crop: approvedCrop,
    select,
  });
  return res.data;
}

export async function skipPhoto(imageId) {
  const res = await axios.patch(`${API_BASE}/photos/${imageId}/`, {
    status: "skipped",
  });
  return res.data;
}

export async function getFtpStatus() {
  const res = await axios.get(`${API_BASE}/photos/ftp_status/`);
  return res.data;
}

export async function ftpUploadPhoto(imageId) {
  const res = await axios.post(`${API_BASE}/photos/${imageId}/ftp_upload/`);
  return res.data;
}

export async function recropPhoto(imageId, crop, rotation = 0, autoFtp = false, select = false) {
  // A Done (+ Wire) with a pending crop routes through recrop, not
  // complete() — the wire choice rides this request and is honored by the
  // machine auto-completion when the pipeline finishes.
  const res = await axios.post(`${API_BASE}/photos/${imageId}/recrop/`, {
    crop,
    rotation,
    auto_ftp: autoFtp,
    select,
    client: 'tank',
  });
  return res.data;
}

export async function saveCropDraft(imageId, box, rotation = 0) {
  // Persist a confirmed crop WITHOUT publishing: appends a draft row to the
  // photo's crop history (zero pipeline side effects). The newest history
  // row is the lead crop both editors open on; Done still publishes via
  // recrop, which appends the matching published row server-side.
  const res = await axios.post(`${API_BASE}/photos/${imageId}/crop_draft/`, {
    box,
    rotation,
    client: 'tank',
  });
  return res.data;
}

export async function createPerson(payload) {
  const res = await axios.post(`${API_BASE}/persons/`, payload);
  return res.data;
}

export async function updatePerson(personId, payload) {
  const res = await axios.patch(`${API_BASE}/persons/${personId}/`, payload);
  return res.data;
}

export async function fetchPerson(personId) {
  const res = await axios.get(`${API_BASE}/persons/${personId}/`);
  return res.data;
}

export async function ignoreFace(faceId) {
  const res = await axios.post(`${API_BASE}/faces/${faceId}/ignore/`);
  return res.data;
}

export async function unignoreFace(faceId) {
  const res = await axios.post(`${API_BASE}/faces/${faceId}/unignore/`);
  return res.data;
}

export async function deleteImage(imageId) {
  const res = await axios.post(`${API_BASE}/faces/delete_image/`, {
    image_id: imageId,
  });
  return res.data;
}

export async function updateCrop(imageId, crop, rotation) {
  const res = await axios.post(`${API_BASE}/faces/update_crop/`, {
    image_id: imageId,
    crop,
    rotation,
  });
  return res.data;
}

export async function savePp3(imageId, settings, name) {
  const res = await axios.post(`${API_BASE}/faces/save_pp3/`, {
    image_id: imageId,
    settings,
    name,
  });
  return res.data;
}

// Optional combined save (crop + adjustments). Falls back to save_pp3/update_crop on the server side if supported.
export async function saveImage(imageId, payload) {
  const res = await axios.post(`${API_BASE}/faces/save_image/`, {
    image_id: imageId,
    ...payload,
  });
  return res.data;
}

export async function assignPerson(faceId, personId, distance) {
  const res = await axios.post(
    `${API_BASE}/faces/${faceId}/assign_person/`,
    { person_id: personId, distance }
  );
  return res.data;
}

export async function propagate(faceId, threshold = 0.35, max_count = 50) {
  const res = await axios.post(
    `${API_BASE}/faces/${faceId}/propagate/`,
    { threshold, max_count }
  );
  return res.data;
}

export async function unassignFace(faceId) {
  const res = await axios.post(`${API_BASE}/faces/${faceId}/unassign_person/`);
  return res.data;
}

export async function getMatchSource(faceId) {
  const res = await axios.get(`${API_BASE}/faces/${faceId}/match_source/`);
  return res.data;
}

export async function removeMatchSource(faceId, cascade = false) {
  const res = await axios.post(
    `${API_BASE}/faces/${faceId}/remove_match_source/`,
    null,
    cascade ? { params: { cascade: 'true' } } : undefined,
  );
  return res.data;
}

export async function archiveBatch(imageIds) {
  const res = await axios.post(`${API_BASE}/photos/archive_batch/`, {
    image_ids: imageIds,
  });
  return res.data;
}

export async function restoreBatch(imageIds) {
  const res = await axios.post(`${API_BASE}/photos/restore_batch/`, {
    image_ids: imageIds,
  });
  return res.data;
}

export async function fetchArchiveStatus() {
  const res = await axios.get(`${API_BASE}/photos/archive_status/`);
  return res.data;
}

export async function fetchDetectionStats(limit = 500) {
  const res = await axios.get(`${API_BASE}/photos/detection_stats/`, { params: { limit } });
  return res.data;
}

export async function fetchMetricsTimeline(hours = 24, bucketMinutes = 15, limit = 500) {
  const res = await axios.get(`${API_BASE}/photos/metrics_timeline/`, {
    params: { hours, bucket_minutes: bucketMinutes, limit }
  });
  return res.data;
}

// Send to Editor functions
export async function sendToEditor(imageId, reason, reasonDetail = "", crop = null, personIds = []) {
  const payload = { reason, reason_detail: reasonDetail };
  if (crop) payload.crop = crop;
  if (personIds.length > 0) payload.person_ids = personIds;
  const res = await axios.post(`${API_BASE}/photos/${imageId}/send_to_editor/`, payload);
  return res.data;
}

export async function sendToEditorBatch(imageIds, reason, reasonDetail = "") {
  const res = await axios.post(`${API_BASE}/photos/send_to_editor_batch/`, {
    image_ids: imageIds,
    reason,
    reason_detail: reasonDetail,
  });
  return res.data;
}

export async function fetchEditorSendsStats(fromDate = null, toDate = null) {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  const res = await axios.get(`${API_BASE}/photos/editor_sends_stats/`, { params });
  return res.data;
}

export async function fetchAIQualityStats(limit = 500) {
  const res = await axios.get(`${API_BASE}/photos/ai_quality_stats/`, {
    params: { limit },
  });
  return res.data;
}

// =============================================================================
// Training API Functions
// =============================================================================

export async function fetchTrainingImages(status = null) {
  const params = {};
  if (status) params.status = status;
  const res = await axios.get(`${API_BASE}/training/`, { params });
  return res.data;
}

export async function fetchTrainingImage(id) {
  const res = await axios.get(`${API_BASE}/training/${id}/`);
  return res.data;
}

export async function fetchTrainingStats() {
  const res = await axios.get(`${API_BASE}/training/stats/`);
  return res.data;
}

export async function fetchTrainingFaces(trainingImageId) {
  const res = await axios.get(`${API_BASE}/training/${trainingImageId}/faces/`);
  return res.data;
}

export async function markTrainingDone(id) {
  const res = await axios.post(`${API_BASE}/training/${id}/done/`);
  return res.data;
}

export async function updateTrainingImage(id, data) {
  const res = await axios.patch(`${API_BASE}/training/${id}/`, data);
  return res.data;
}

export async function deleteTrainingImage(id) {
  const res = await axios.delete(`${API_BASE}/training/${id}/`);
  return res.data;
}

export async function deleteUntrainedImages() {
  const res = await axios.delete(`${API_BASE}/training/delete_untrained/`);
  return res.data;
}

export async function uploadTrainingImages(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const res = await axios.post(`${API_BASE}/training/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function fetchPropagateCandidates(threshold = 0.5, topK = 10) {
  const res = await axios.get(`${API_BASE}/training/propagate_candidates/`, {
    params: { threshold, top_k: topK },
  });
  return res.data;
}

export async function bulkAssignPerson(faceIds, personId) {
  // Assign person to multiple faces
  const results = await Promise.all(
    faceIds.map((faceId) =>
      axios.post(`${API_BASE}/faces/${faceId}/assign_person/`, { person_id: personId })
    )
  );
  return results.map((r) => r.data);
}

export async function fetchSystemStatus() {
  const res = await axios.get(`${API_BASE}/system/status/`);
  return res.data;
}

// =============================================================================
// Team API Functions
// =============================================================================

export async function fetchTeams(params = {}) {
  const res = await axios.get(`${API_BASE}/teams/`, { params, withCredentials: true });
  return res.data;
}

export async function searchTeams(query) {
  const res = await axios.get(`${API_BASE}/teams/`, {
    params: { search: query, limit: 10 },
    withCredentials: true,
  });
  return res.data?.results || res.data || [];
}

export async function fetchTeam(teamId) {
  const res = await axios.get(`${API_BASE}/teams/${teamId}/`, { withCredentials: true });
  return res.data;
}

export async function createTeam(name, teamType = "club") {
  const res = await axios.post(
    `${API_BASE}/teams/`,
    { name, team_type: teamType },
    { withCredentials: true }
  );
  return res.data;
}

export async function updateTeam(teamId, data) {
  const res = await axios.patch(
    `${API_BASE}/teams/${teamId}/`,
    data,
    { withCredentials: true }
  );
  return res.data;
}

export async function deleteTeam(teamId) {
  const res = await axios.delete(
    `${API_BASE}/teams/${teamId}/`,
    { withCredentials: true }
  );
  return res.data;
}

export async function fetchTeamMembers(teamId) {
  const res = await axios.get(
    `${API_BASE}/teams/${teamId}/members/`,
    { withCredentials: true }
  );
  return res.data;
}

export async function addTeamMember(teamId, personId, jerseyNumber = null) {
  const res = await axios.post(
    `${API_BASE}/teams/${teamId}/add_member/`,
    { person_id: personId, jersey_number: jerseyNumber },
    { withCredentials: true }
  );
  return res.data;
}

export async function removeTeamMember(teamId, personId) {
  const res = await axios.post(
    `${API_BASE}/teams/${teamId}/remove_member/`,
    { person_id: personId },
    { withCredentials: true }
  );
  return res.data;
}

// =============================================================================
// Match API Functions
// =============================================================================

export async function fetchMatches(params = {}) {
  const res = await axios.get(`${API_BASE}/matches/`, { params, withCredentials: true });
  return res.data;
}

// /api/matches/ is paginated (LimitOffsetPagination, page size 50) and ordered
// -start_time, so a bare fetchMatches() returns only the 50 newest matches and
// anything older is invisible to callers that treat page 1 as "all matches".
// This walks every page and returns the flat array.
export async function fetchAllMatches(params = {}) {
  const limit = 200;
  const all = [];
  // Defensive cap (50 pages = 10k matches) so a server that keeps reporting
  // `next` can never loop us forever.
  for (let page = 0; page < 50; page++) {
    const res = await axios.get(`${API_BASE}/matches/`, {
      params: { ...params, limit, offset: page * limit },
      withCredentials: true,
    });
    const data = res.data;
    const results = data.results || data || [];
    all.push(...results);
    if (!data.next || results.length === 0) break;
  }
  return all;
}

export async function fetchMatch(matchId) {
  const res = await axios.get(`${API_BASE}/matches/${matchId}/`, { withCredentials: true });
  return res.data;
}

export async function createMatch(data) {
  const res = await axios.post(
    `${API_BASE}/matches/`,
    data,
    { withCredentials: true }
  );
  return res.data;
}

export async function updateMatch(matchId, data) {
  const res = await axios.patch(
    `${API_BASE}/matches/${matchId}/`,
    data,
    { withCredentials: true }
  );
  return res.data;
}

export async function deleteMatch(matchId) {
  const res = await axios.delete(
    `${API_BASE}/matches/${matchId}/`,
    { withCredentials: true }
  );
  return res.data;
}

// --- MatchHub integration (ADR-005 P4) -------------------------------------

// List a MatchHub season's fixtures (proxy; key stays server-side). MatchHub has
// no free-text search, so a season_id is required; callers filter client-side.
export async function fetchMatchHubFixtures(seasonId, status) {
  const res = await axios.get(`${API_BASE}/matchhub/fixtures/`, {
    params: { season_id: seasonId, ...(status ? { status } : {}) },
    withCredentials: true,
  });
  return res.data;
}

// Link a match to a MatchHub fixture (stores ids + syncs events).
export async function linkMatchHubFixture(matchId, fixtureId, seasonId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/link_matchhub/`,
    { matchhub_fixture_id: fixtureId, matchhub_season_id: seasonId },
    { withCredentials: true }
  );
  return res.data;
}

// Name reconciliation (suggest+confirm) for persons and teams. `resource` is
// "persons" or "teams"; confirm accepts an optional edited name.
export async function refreshMatchHubSuggestion(resource, id) {
  const res = await axios.post(
    `${API_BASE}/${resource}/${id}/refresh_matchhub_suggestion/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function confirmMatchHubName(resource, id, name) {
  const res = await axios.post(
    `${API_BASE}/${resource}/${id}/confirm_matchhub_name/`,
    name ? { name } : {},
    { withCredentials: true }
  );
  return res.data;
}

export async function dismissMatchHubSuggestion(resource, id) {
  const res = await axios.post(
    `${API_BASE}/${resource}/${id}/dismiss_matchhub_suggestion/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

// --- MatchHub round 2: free-text search + confirmed-link flows ---------------

// Free-text fixture search (replaces the season-id-only flow above). Returns
// {count, query, matches:[{id, status, kickoff, home, away, goals, round,
// venue_id, season:{id, year, competition:{...}}}]}.
export async function searchMatchHubFixtures(query) {
  const res = await axios.get(`${API_BASE}/matchhub/fixtures/search/`, {
    params: { q: query },
    withCredentials: true,
  });
  return res.data;
}

// Free-text MatchHub team search. `currentOnly` restricts to tracked-current
// season teams (good default for the match wizard's create-team-with-link path).
export async function searchMatchHubTeams(query, { currentOnly = false } = {}) {
  const res = await axios.get(`${API_BASE}/matchhub/teams/search/`, {
    params: { q: query, ...(currentOnly ? { current: "true" } : {}) },
    withCredentials: true,
  });
  return res.data;
}

// Free-text MatchHub player search. `teamExternalId` (optional) restricts to
// players appearing in any tracked lineup of that team — used by the manual
// per-row search fallback in BulkLinkMatchHubModal.
export async function searchMatchHubPlayers(query, { teamExternalId = null } = {}) {
  const res = await axios.get(`${API_BASE}/matchhub/players/search/`, {
    params: {
      q: query,
      ...(teamExternalId ? { team: teamExternalId } : {}),
    },
    withCredentials: true,
  });
  return res.data;
}

// MatchHub Career History panel (per Person). Returns the raw transfer payload
// {player:{id,name}, transfers:[...]} from API-Football via matchhub.
export async function fetchMatchHubPlayerTransfers(playerExternalId) {
  const res = await axios.get(
    `${API_BASE}/matchhub/players/${encodeURIComponent(playerExternalId)}/transfers/`,
    { withCredentials: true }
  );
  return res.data;
}

// Resolve a MatchHub team external_id to a local Team (or null when absent).
// Lets the match wizard decide whether to offer the inline create-with-override
// card for missing home/away teams.
export async function fetchTeamByExternalId(externalId) {
  const res = await axios.get(`${API_BASE}/teams/`, {
    params: { external_id: externalId },
    withCredentials: true,
  });
  const rows = res.data?.results || res.data || [];
  return Array.isArray(rows) ? rows[0] || null : null;
}

// Link a local Team to a MatchHub team + persist the per-company override
// (CompanyTeamNaming.confirmed_name). Also opt-in pushes the override back to
// MatchHub when the chosen name diverges from MatchHub's resolution.
export async function linkTeamToMatchHub(teamId, externalId, confirmedName) {
  const res = await axios.post(
    `${API_BASE}/teams/${teamId}/link_matchhub/`,
    { external_id: externalId, confirmed_name: confirmedName },
    { withCredentials: true }
  );
  return res.data;
}

// Same shape for Person → MatchHub Player linking (single-person path).
export async function linkPersonToMatchHub(personId, externalId, confirmedName) {
  const res = await axios.post(
    `${API_BASE}/persons/${personId}/link_matchhub/`,
    { external_id: externalId, confirmed_name: confirmedName },
    { withCredentials: true }
  );
  return res.data;
}

// Step 1 of the bulk-link wizard: get per-Person MatchHub candidate suggestions
// for a (locally-linked) Team. Validates server-side that the team has an
// external_id; returns 400 + a hint otherwise.
export async function bulkLinkMatchHubSuggest(teamId) {
  const res = await axios.post(
    `${API_BASE}/persons/bulk_link_matchhub_suggest/`,
    { team_id: teamId },
    { withCredentials: true }
  );
  return res.data;
}

// Step 2 of the bulk-link wizard: confirm a batch of Person→Player links. Each
// row {person_id, matchhub_player_id, confirmed_name} is processed
// independently with per-row status.
export async function bulkLinkMatchHubConfirm(rows) {
  const res = await axios.post(
    `${API_BASE}/persons/bulk_link_matchhub_confirm/`,
    { rows },
    { withCredentials: true }
  );
  return res.data;
}

// Per-match club affiliations (ADR-005 P6): which club a tagged person represents
// in THIS match (temporal — handles spectators/loanees/since-transferred players).
export async function fetchAffiliations(matchId) {
  const res = await axios.get(`${API_BASE}/matches/${matchId}/affiliations/`, {
    withCredentials: true,
  });
  return res.data?.affiliations || [];
}

export async function refreshAffiliations(matchId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/refresh_affiliations/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function confirmAffiliation(matchId, personId, teamName) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/confirm_affiliation/`,
    teamName ? { person_id: personId, team_name: teamName } : { person_id: personId },
    { withCredentials: true }
  );
  return res.data;
}

export async function dismissAffiliation(matchId, personId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/dismiss_affiliation/`,
    { person_id: personId },
    { withCredentials: true }
  );
  return res.data;
}

// --- Lineup sheet scan (lineup OCR v2) ---------------------------------------
// Contract: matrix/docs/lineup-scan-contract.md. Tank REVIEWS a confirmed
// scan (the app confirms); the scan resource is the evidence document behind
// the source="sheet" affiliations. 404 = no confirmed scan (empty state).

export async function fetchLineupScan(matchId) {
  const res = await axios.get(`${API_BASE}/matches/${matchId}/lineup-scan/`, {
    withCredentials: true,
  });
  return res.data;
}

// Bridge from an unmatched sheet row to a (possibly just-created) person.
// `side` undefined (omitted) when the person appears in the scan's stored
// roster: the server falls back to that pool and 400s when neither names a
// side. An EXPLICIT null is the official path (06-09): the key is SENT as
// null, the person is linked with no team and the row files as staff.
// Returns the updated scan resource (the GET shape).
export async function resolveLineupRow(matchId, rowIndex, personId, side) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/lineup-scan/resolve-row/`,
    {
      row_index: rowIndex,
      person_id: personId,
      ...(side !== undefined ? { side } : {}),
    },
    { withCredentials: true }
  );
  return res.data;
}

// Retract (wrong-match scan escape hatch): removes the scan row, the sheet
// file and every source="sheet" affiliation. Requires can_confirm_lineup —
// the server 403s everyone else.
export async function deleteLineupScan(matchId) {
  const res = await axios.delete(`${API_BASE}/matches/${matchId}/lineup-scan/`, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Pin a Caption Builder chain to the match's quick-actions.
 * Body: { label, template }. Returns the new pinned object including its id.
 */
export async function pinMatchQuickCaption(matchId, { label, template }) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/quick-captions/`,
    { label, template },
    { withCredentials: true },
  );
  return res.data;
}

/** Remove a pinned quick caption from a match. */
export async function unpinMatchQuickCaption(matchId, capId) {
  const res = await axios.delete(
    `${API_BASE}/matches/${matchId}/quick-captions/${capId}/`,
    { withCredentials: true },
  );
  return res.data;
}

export async function assignPhotographerToMatch(matchId, userId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/assign/`,
    { user_id: userId },
    { withCredentials: true }
  );
  return res.data;
}

export async function unassignPhotographerFromMatch(matchId, userId) {
  const res = await axios.delete(
    `${API_BASE}/matches/${matchId}/unassign/`,
    { params: { user_id: userId }, withCredentials: true }
  );
  return res.data;
}

export async function assignEditorToMatch(matchId, userId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/assign_editor/`,
    { user_id: userId },
    { withCredentials: true }
  );
  return res.data;
}

export async function unassignEditorFromMatch(matchId, userId) {
  const res = await axios.delete(
    `${API_BASE}/matches/${matchId}/unassign_editor/`,
    { params: { user_id: userId }, withCredentials: true }
  );
  return res.data;
}

export async function markMatchFullTime(matchId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/full-time/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function undoMatchFullTime(matchId) {
  const res = await axios.post(
    `${API_BASE}/matches/${matchId}/undo-full-time/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function fetchMatchPhotos(matchId, params = {}) {
  const res = await axios.get(
    `${API_BASE}/matches/${matchId}/photos/`,
    { params, withCredentials: true }
  );
  return res.data;
}

export function getMatchXmpUrl(matchId) {
  return `${API_BASE}/matches/${matchId}/download-xmp/`;
}

// =============================================================================
// User API Functions (for photographer list)
// =============================================================================

export async function fetchUsers(params = {}) {
  const res = await axios.get(`${API_BASE}/users/`, { params, withCredentials: true });
  return res.data;
}

export async function createUser(data) {
  const res = await axios.post(`${API_BASE}/users/`, data, { withCredentials: true });
  return res.data;
}

export async function updateUser(userId, data) {
  const res = await axios.patch(`${API_BASE}/users/${userId}/`, data, { withCredentials: true });
  return res.data;
}

export async function deleteUser(userId) {
  await axios.delete(`${API_BASE}/users/${userId}/`, { withCredentials: true });
}

// =============================================================================
// Person API Functions
// =============================================================================

export async function fetchPersons(params = {}) {
  const res = await axios.get(`${API_BASE}/persons/`, { params, withCredentials: true });
  return res.data;
}

export async function deletePerson(personId) {
  await axios.delete(`${API_BASE}/persons/${personId}/`, { withCredentials: true });
}

export async function mergePersons(data) {
  const res = await axios.post(`${API_BASE}/persons/merge/`, data, { withCredentials: true });
  return res.data;
}

// BT-5 — Recent-activity feed for the Manage Overview dashboard.
export async function fetchManageActivity(limit = 20) {
  const res = await axios.get(`${API_BASE}/manage/activity/`, {
    params: { limit },
    withCredentials: true,
  });
  return res.data;
}

// Statistics — Performance + Timeline tabs.
// Contracts: /Users/yannick/Downloads/HANDOFF-stats-perf-timeline.md.

export async function fetchStatsOverview(range = '30d', includeArchived = false) {
  const res = await axios.get(`${API_BASE}/stats/overview/`, {
    params: { range, include_archived: includeArchived ? 'true' : 'false' },
    withCredentials: true,
  });
  return res.data;
}

export async function fetchStatsPerformance(range = '30d', includeArchived = false) {
  const res = await axios.get(`${API_BASE}/stats/performance/`, {
    params: { range, include_archived: includeArchived ? 'true' : 'false' },
    withCredentials: true,
  });
  return res.data;
}

export async function fetchStatsSlowSteps(range = '30d', limit = 8, includeArchived = false) {
  const res = await axios.get(`${API_BASE}/stats/slow-steps/`, {
    params: { range, limit, include_archived: includeArchived ? 'true' : 'false' },
    withCredentials: true,
  });
  return res.data;
}

export async function fetchPhotoTimeline(imageId) {
  const res = await axios.get(`${API_BASE}/timeline/${imageId}/`, {
    withCredentials: true,
  });
  return res.data;
}

// =============================================================================
// Role API Functions
// =============================================================================

export async function fetchRoles(params = {}) {
  const res = await axios.get(`${API_BASE}/roles/`, { params, withCredentials: true });
  return res.data;
}

export async function createRole(data) {
  const res = await axios.post(`${API_BASE}/roles/`, data, { withCredentials: true });
  return res.data;
}

export async function updateRole(roleId, data) {
  const res = await axios.patch(`${API_BASE}/roles/${roleId}/`, data, { withCredentials: true });
  return res.data;
}

export async function deleteRole(roleId) {
  await axios.delete(`${API_BASE}/roles/${roleId}/`, { withCredentials: true });
}

// =============================================================================
// FTP Source API Functions (used by UserList's FTP section)
// =============================================================================

export async function createFtpSource(data) {
  const res = await axios.post(`${API_BASE}/ftp-sources/`, data, { withCredentials: true });
  return res.data;
}

export async function updateFtpSource(sourceId, data) {
  const res = await axios.patch(`${API_BASE}/ftp-sources/${sourceId}/`, data, { withCredentials: true });
  return res.data;
}

// =============================================================================
// FTP Target API Functions
// =============================================================================

export async function fetchFtpTargets(params = {}) {
  const res = await axios.get(`${API_BASE}/ftp-targets/`, { params, withCredentials: true });
  return res.data;
}

export async function createFtpTarget(data) {
  const res = await axios.post(`${API_BASE}/ftp-targets/`, data, { withCredentials: true });
  return res.data;
}

export async function updateFtpTarget(targetId, data) {
  const res = await axios.patch(`${API_BASE}/ftp-targets/${targetId}/`, data, { withCredentials: true });
  return res.data;
}

export async function deleteFtpTarget(targetId) {
  await axios.delete(`${API_BASE}/ftp-targets/${targetId}/`, { withCredentials: true });
}

export async function testFtpTarget(data) {
  const res = await axios.post(`${API_BASE}/ftp-targets/test/`, data, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Profile API Functions (extended)
// =============================================================================

export async function setDefaultProfile(profileId) {
  const res = await axios.post(`${API_BASE}/profiles/${profileId}/set-default/`, {}, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Company Settings API Functions
// =============================================================================

export async function fetchCompanyStats() {
  const res = await axios.get(`${API_BASE}/company/stats/`, { withCredentials: true });
  return res.data;
}

export async function fetchCompanySettings() {
  const res = await axios.get(`${API_BASE}/company/settings/`, { withCredentials: true });
  return res.data;
}

export async function updateCompanySettings(data) {
  const res = await axios.patch(`${API_BASE}/company/settings/`, data, { withCredentials: true });
  return res.data;
}

// =============================================================================
// FTP Target Selection API Functions (for Match Wizard)
// =============================================================================

export async function fetchFtpTargetsForSelection() {
  const res = await axios.get(`${API_BASE}/ftp-targets/for-match-selection/`, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Grade API Functions (export styles)
// =============================================================================

export async function fetchGrades() {
  const res = await axios.get(`${API_BASE}/grades/`, { withCredentials: true });
  return res.data;
}

// =============================================================================
// IPTC Template API Functions (per-destination metadata)
// =============================================================================

export async function fetchIptcTemplates() {
  const res = await axios.get(`${API_BASE}/iptc-templates/`, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Company IPTC Defaults API Functions
// =============================================================================

export async function fetchCompanyIptcDefaults() {
  const res = await axios.get(`${API_BASE}/company/iptc-defaults/`, { withCredentials: true });
  return res.data;
}

export async function updateCompanyIptcDefaults(payload) {
  const res = await axios.put(`${API_BASE}/company/iptc-defaults/`, payload, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Caption Builder Config API Functions
// =============================================================================

export async function fetchCaptionBuilderConfig() {
  const res = await axios.get(`${API_BASE}/company/caption-builder-config/`, { withCredentials: true });
  return res.data;
}

export async function updateCaptionBuilderConfig(data) {
  const res = await axios.put(`${API_BASE}/company/caption-builder-config/`, data, { withCredentials: true });
  return res.data;
}

export async function resetCaptionBuilderConfig() {
  const res = await axios.post(`${API_BASE}/company/caption-builder-config/reset/`, {}, { withCredentials: true });
  return res.data;
}

// =============================================================================
// IPTC Preview API Functions
// =============================================================================

export async function fetchIptcPreview(templates, context = null, matchId = null) {
  const payload = { ...templates };
  if (context) payload.context = context;
  // The server returns the keywords the EXPORTER merges in on top of the
  // configured ones. Without the match it cannot know them, and step 3 goes
  // back to under-reporting every destination's keyword set.
  if (matchId) payload.match_id = matchId;
  const res = await axios.post(`${API_BASE}/iptc/preview/`, payload, { withCredentials: true });
  return res.data;
}

// =============================================================================
// Photo Export API Functions
// =============================================================================

export async function fetchPhotoExports(imageId, includeHistory = false) {
  const params = includeHistory ? { include_history: 'true' } : {};
  const res = await axios.get(`${API_BASE}/photos/${imageId}/exports/`, {
    params,
    withCredentials: true,
  });
  return res.data;
}

export async function retryPhotoExport(imageId, exportId) {
  const res = await axios.post(
    `${API_BASE}/photos/${imageId}/exports/${exportId}/retry/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function retryAllPhotoExports(imageId) {
  const res = await axios.post(
    `${API_BASE}/photos/${imageId}/exports/retry-all/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function exportVersion(imageId, cropVersion, cropBox, ftpTargetId) {
  const res = await axios.post(
    `${API_BASE}/photos/${imageId}/export-version/`,
    {
      crop_version: cropVersion,
      crop_box: cropBox,
      ftp_target_id: ftpTargetId,
    },
    { withCredentials: true }
  );
  return res.data;
}

// =============================================================================
// System Analytics Dashboard API Functions
// =============================================================================

export async function fetchClusterOverview() {
  const res = await axios.get(`${API_BASE}/system/cluster/overview/`, { withCredentials: true });
  return res.data;
}

// Machine Management
export async function fetchMachines() {
  const res = await axios.get(`${API_BASE}/system/machines/`, { withCredentials: true });
  return res.data;
}

export async function fetchMachine(hostname) {
  const res = await axios.get(`${API_BASE}/system/machines/${hostname}/`, { withCredentials: true });
  return res.data;
}

export async function createMachine(data) {
  const res = await axios.post(`${API_BASE}/system/machines/create/`, data, { withCredentials: true });
  return res.data;
}

export async function deleteMachine(hostname) {
  const res = await axios.delete(`${API_BASE}/system/machines/${hostname}/delete/`, { withCredentials: true });
  return res.data;
}

export async function restartContainer(hostname, containerName) {
  const res = await axios.post(
    `${API_BASE}/system/machines/${hostname}/containers/${containerName}/restart/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function stopContainer(hostname, containerName) {
  const res = await axios.post(
    `${API_BASE}/system/machines/${hostname}/containers/${containerName}/stop/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

// Queue Management
export async function fetchQueues() {
  const res = await axios.get(`${API_BASE}/system/queues/`, { withCredentials: true });
  return res.data;
}

export async function fetchQueueJobs(queueName, offset = 0, limit = 50) {
  const res = await axios.get(`${API_BASE}/system/queues/${queueName}/jobs/`, {
    params: { offset, limit },
    withCredentials: true,
  });
  return res.data;
}

export async function deleteQueueJob(queueName, jobIndex) {
  const res = await axios.delete(
    `${API_BASE}/system/queues/${queueName}/jobs/${jobIndex}/`,
    { withCredentials: true }
  );
  return res.data;
}

export async function flushQueue(queueName) {
  const res = await axios.post(
    `${API_BASE}/system/queues/${queueName}/flush/`,
    { confirm: 'FLUSH' },
    { withCredentials: true }
  );
  return res.data;
}

// Dead Letter Queue
export async function fetchDLQ() {
  const res = await axios.get(`${API_BASE}/system/queues/dlq/`, { withCredentials: true });
  return res.data;
}

export async function retryDLQJob(jobIndex) {
  const res = await axios.post(
    `${API_BASE}/system/queues/dlq/${jobIndex}/retry/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function retryAllDLQ() {
  const res = await axios.post(
    `${API_BASE}/system/queues/dlq/retry-all/`,
    { confirm: 'RETRY_ALL' },
    { withCredentials: true }
  );
  return res.data;
}

// Alerts
export async function fetchAlerts(status = 'active', limit = 50) {
  const res = await axios.get(`${API_BASE}/system/alerts/`, {
    params: { status, limit },
    withCredentials: true,
  });
  return res.data;
}

export async function acknowledgeAlert(alertId) {
  const res = await axios.post(
    `${API_BASE}/system/alerts/${alertId}/acknowledge/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function fetchAlertChannels() {
  const res = await axios.get(`${API_BASE}/system/alerts/channels/`, { withCredentials: true });
  return res.data;
}

// Sessions (for traffic map)
export async function fetchActiveSessions() {
  const res = await axios.get(`${API_BASE}/system/sessions/active/`, { withCredentials: true });
  return res.data;
}

// =============================================================================
// User Language API
// =============================================================================

export async function updateMyLanguage(language) {
  const res = await axios.patch(
    `${API_BASE}/auth/me/`,
    { language },
    { withCredentials: true }
  );
  return res.data;
}

// PATCH a partial preferences object — backend merges with the existing
// user.preferences blob so other keys are preserved.
export async function updateMyPreferences(preferences) {
  const res = await axios.patch(
    `${API_BASE}/auth/me/`,
    { preferences },
    { withCredentials: true }
  );
  return res.data;
}

// =============================================================================
// Notification API Functions
// =============================================================================

export async function fetchNotifications() {
  const res = await axios.get(`${API_BASE}/notifications/`, { withCredentials: true });
  return res.data;
}

export async function fetchUnreadNotificationCount() {
  const res = await axios.get(`${API_BASE}/notifications/unread-count/`, { withCredentials: true });
  return res.data;
}

export async function markNotificationRead(notificationId) {
  const res = await axios.post(
    `${API_BASE}/notifications/${notificationId}/read/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function dismissNotification(notificationId) {
  const res = await axios.post(
    `${API_BASE}/notifications/${notificationId}/dismiss/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

export async function markAllNotificationsRead() {
  const res = await axios.post(
    `${API_BASE}/notifications/read-all/`,
    {},
    { withCredentials: true }
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Competitions + venues (database-backed since operator migration 0108)
// ---------------------------------------------------------------------------

/** Every competition row, unpaginated. `{ active: true }` for the wizard. */
export async function fetchCompetitions(params = {}) {
  const res = await axios.get(`${API_BASE}/competitions/`, { params, withCredentials: true });
  return res.data;
}

export async function createCompetition(data) {
  const res = await axios.post(`${API_BASE}/competitions/`, data, { withCredentials: true });
  return res.data;
}

export async function updateCompetition(competitionId, data) {
  const res = await axios.patch(`${API_BASE}/competitions/${competitionId}/`, data, {
    withCredentials: true,
  });
  return res.data;
}

/** 409 with `{ detail, match_count }` when matches still reference the slug. */
export async function deleteCompetition(competitionId) {
  await axios.delete(`${API_BASE}/competitions/${competitionId}/`, { withCredentials: true });
}

/** Stadium lookup by name, alias, resident club or city. */
export async function fetchVenues(params = {}) {
  const res = await axios.get(`${API_BASE}/venues/`, { params, withCredentials: true });
  return res.data;
}

// ---------------------------------------------------------------------------
// Voice notes (camera memos, phone / watch notes): vault Matrix Voice Notes
// ---------------------------------------------------------------------------

export async function fetchVoiceNotes(matchId) {
  const res = await axios.get(`${API_BASE}/matches/${matchId}/voice-notes/`, { withCredentials: true });
  return res.data.notes || [];
}

export async function deleteVoiceNote(noteId) {
  await axios.delete(`${API_BASE}/voice-notes/${noteId}/`, { withCredentials: true });
}

/**
 * Mark a voice note dealt with, or put it back.
 *
 * Shared state on the note itself, not a local hide: a match note is one
 * piece of work for the whole desk, so a note finished in the app is
 * finished here too.
 */
export async function setVoiceNoteHandled(noteId, handled = true) {
  const res = await axios.post(
    `${API_BASE}/voice-notes/${noteId}/handled/`,
    { handled },
    { withCredentials: true },
  );
  return res.data;
}

export function voiceNoteAudioUrl(noteId) {
  return `${API_BASE}/voice-notes/${noteId}/audio/`;
}
